// src/components/RecordPlayer/VideoPlayer.jsx
import { useRef, useEffect, useState, use } from 'react';
import ReactPlayer from 'react-player';
import { isSafari, ensureSafariVideoPlayback, isUCBrowser } from '../../utils/browserUtils';
import { formatTime } from '../../utils/timeUtils';
import { MAX_VIDEO_PLAY_OFFSET } from '../../constant/Config';

const VideoPlayer = ({ videoInfo, teacherInfo, currentTime, isPlaying, playbackRate, isMediaReady, onVideoStateChange }) => {

  const videoRef = useRef(null);
  const currentVideoIndexRef = useRef(-1);
  
  const [videoUrl, setVideoUrl] = useState(null);
  const [offset, setOffset] = useState(0);
  const [currentVideoDuration, setCurrentVideoDuration] = useState(-1);
  const [isVideoEnablePlay, setIsVideoEnablePlay] = useState(false);
  const [isVideoLoadMeta, setIsVideoLoadMeta] = useState(false);

  const [isSeeking, setIsSeeking] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [lastBufferStart, setLastBufferStart] = useState(null);
  
  const bufferTimerRef = useRef(null);

  const preSeekingTime = useRef(null);
  const DEBOUNCE_DELAY = 2000;
  const BUFFER_THRESHOLD = 200; // 缓冲显示阈值(ms)
  
  const audioLogsTextElement = document.getElementById('video-logs-text');
  const updateLog = (log) => {
    if (audioLogsTextElement) {
      audioLogsTextElement.innerHTML += `[${formatTime(currentTime)}]${log} <br>`;
    }
  };

  const ensureNormalPlaybackRate = () => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  };

  const formatNumber = (num) => {
    return num.toFixed(5);
  };

  // utils/unlockMedia.js
  let mediaUnlocked = false;
  const unlockIOSMedia = () => {
    if (mediaUnlocked) return;
    if (!/iPad|iPhone|iPod/.test(navigator.userAgent)) return;

    const unlock = () => {
      const video = document.createElement('video');
      video.muted = true;
      video.src = 'data:video/mp4;base64,AAAA...'; // 极短的 base64 视频
      const playPromise = video.play()
      if (playPromise) {
        playPromise.then(() => {
          // updateLog('iOS 媒体解锁成功');
        }).catch((error) => {
          // updateLog('Auto-play prevented:' + error);
        });
      }
    };

    document.body.addEventListener('touchstart', unlock, { once: true });
  };

  // 在组件或 App.js 中调用
  useEffect(() => {

    unlockIOSMedia();

    // 如果视频不为空，则先放出来进行初始化
    if (videoInfo && videoInfo.length > 0) {
      setVideoUrl(videoInfo[0].url);
      setOffset(videoInfo[0].milliseconds);
      setCurrentVideoDuration(-1);
      currentVideoIndexRef.current = 0;
    }

  }, []);

  useEffect(() => { 
    
    updateLog(`[onMediaReadyChange]: ${isMediaReady}`);

    if (!isMediaReady) {
      if(isPlaying) {
        handleVideoPause()
      }
      return;
    }

    if (isPlaying) {
      // 播放
      handleVideoPlay()
    } else {
      // 暂停
      handleVideoPause()
    }
  }, [isMediaReady]);

  const handleVideoPlay = () => { 

    updateLog("开始尝试播放视频");

    if(videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise) {
        playPromise.then(() => {
          updateLog('视频播放成功');
        }).catch((error) => {
          updateLog('Auto-play prevented:' + error);
        });
      }
    }
  };

  const handleVideoPause = () => { 
    if(videoRef.current) {
      videoRef.current.pause();
    }
  };

  // 解析当前应播放的视频片段
  useEffect(() => {
    if (videoInfo && videoInfo.length > 0) {
      let targetIndex = -1;
      for (let i = videoInfo.length - 1; i >= 0; i--) {
        if (videoInfo[i].milliseconds <= currentTime + 3) {
          targetIndex = i;
          break;
        }
      }

      const video = videoInfo[targetIndex];
      if (targetIndex !== -1 && targetIndex !== currentVideoIndexRef.current && video.url !== videoUrl) {

        setVideoUrl(video.url);
        setOffset(video.milliseconds);
        setCurrentVideoDuration(-1);
        currentVideoIndexRef.current = targetIndex;
      }
    }
  }, [videoInfo, currentTime]);

  // 根据视频时长、偏移、当前时间判断是否可播放
  useEffect(() => {

    // updateLog(`[VideoPlayer] videoUrl: ${videoUrl}, offset: ${offset}, currentVideoDuration: ${currentVideoDuration}, currentTime: ${currentTime}`);

    if (!videoUrl) {
      setIsVideoEnablePlay(false);
      return;
    }

    // 如果视频未加载完成，则放开可以播放，避免iOS等设备无法播放
    if (currentVideoDuration === -1) {
      setIsVideoEnablePlay(true);
      return;
    }

    const start = offset;
    const end = offset + currentVideoDuration;

    if (currentTime >= start && currentTime <= end) {
      setIsVideoEnablePlay(true);
    } else {
      setIsVideoEnablePlay(false);
    }
  }, [videoUrl, offset, currentVideoDuration, currentTime]);

  // 控制播放/暂停
  useEffect(() => {
    
    updateLog('[VideoPlayer] isVideoEnablePlay:'+ isVideoEnablePlay);

    if (isVideoEnablePlay && isPlaying && videoRef.current) {
      handleVideoPlay()
    } else if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [isVideoEnablePlay, isPlaying]);

  // 监听元数据加载 - 修改为支持MediaSource
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !videoUrl) return;

    videoEl.src = videoUrl;
    videoEl.load();

    const handleLoadedMetadata = () => {
      // updateLog(`[Metadata Loaded]${videoEl.duration}-${currentVideoDuration}`);
      if(currentVideoDuration === -1) {
        setCurrentVideoDuration(videoEl.duration);
        setIsVideoLoadMeta(true)
        ensureNormalPlaybackRate();
      }
    };

    const handleCanPlayThrough = () => {
      if (currentVideoDuration === -1) {
        // updateLog('[Fallback] canplaythrough' + videoEl.duration);
        setCurrentVideoDuration(videoEl.duration);
        setIsVideoLoadMeta(true)
      }
    };

    const handleError = () => {
      // updateLog('[Video] 加载失败' + videoEl.error);
      setIsVideoEnablePlay(false);
    };

    videoEl.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoEl.addEventListener('canplaythrough', handleCanPlayThrough);
    videoEl.addEventListener('error', handleError);

    return () => {
      videoEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoEl.removeEventListener('canplaythrough', handleCanPlayThrough);
      videoEl.removeEventListener('error', handleError);
    };
  }, [videoUrl]);

  useEffect(() => { 

    updateLog('[VideoPlayer]isVideoReady: ' + isVideoReady);

    onVideoStateChange(isVideoReady);

  }, [isVideoReady]);

  // 同步播放状态和跳转时间
  useEffect(() => {

    if (!videoRef.current || !isVideoEnablePlay) return;

    const videoPlayTime = currentTime - offset;
    const currentVideoTime = videoRef.current.currentTime;
    const videoPlayOffset = currentVideoTime - videoPlayTime;
    
    // updateLog(`v:${formatNumber(videoPlayOffset)}, currentVideoTime: ${formatNumber(currentVideoTime)}, videoPlayTime: ${formatNumber(videoPlayTime)}`)

    const videoPlayOffsetElement = document.getElementById('video-play-offset');
    if (videoPlayOffsetElement) {
      videoPlayOffsetElement.textContent = `v:${formatNumber(videoPlayOffset)}, currentVideoTime: ${formatNumber(currentVideoTime)}, videoPlayTime: ${formatNumber(videoPlayTime)}`;
    }

    // 设置倍速
    ensureNormalPlaybackRate();

    if (Math.abs(videoPlayOffset) > MAX_VIDEO_PLAY_OFFSET && videoPlayTime >= 0) {

      if (Math.abs(videoPlayOffset) < 1) {
        if (!preSeekingTime.current || Date.now() - preSeekingTime.current > DEBOUNCE_DELAY) {

          // 如果正在Seeking中，则不允许再次Seek
          if (!isSeeking) {
            videoRef.current.currentTime = videoPlayTime;
            preSeekingTime.current = Date.now();
            // updateLog(`[VideoPlayer] Seeking to: ${videoPlayTime.toFixed(2)}s`);
          }

        }
      } else {
          // 如果正在Seeking中，则不允许再次Seek
          if (!isSeeking) {
            videoRef.current.currentTime = videoPlayTime;
            preSeekingTime.current = Date.now();
            // updateLog(`[VideoPlayer] Seeking (forced): ${videoPlayTime.toFixed(2)}s`);
          }
      }
    }

    if (isSafari()) {
      ensureSafariVideoPlayback(videoRef.current);
    }
  }, [currentTime, isPlaying, offset, isVideoEnablePlay, playbackRate]);

  const checkVideoCacheEnabled = () => { 

    const videoCurrentTime = videoRef.current.currentTime;
    const buffered = videoRef.current.buffered;
    const duration = videoRef.current.duration;

    if (buffered.length > 0 && duration > 0 && videoCurrentTime >= 0) {
      // 获取最后一段已缓冲的范围（通常是最完整的）
      const bufferedEnd = buffered.end(buffered.length - 1);
      const bufferedPercent = (bufferedEnd / duration) * 100;

      const playPercent = (videoCurrentTime / duration) * 100;

      updateLog(`videoPlayProgress：${playPercent}, videoBufferProgress：${bufferedPercent.toFixed(2)}`)
      
      if (playPercent >= 99.8 || bufferedPercent - playPercent >= 0.2) {
        setIsVideoReady(true)
      } else {
        setIsVideoReady(false)
      }
    }
  };

  // 处理缓冲开始
  const handleBufferStart = () => {

    // 记录缓冲开始时间
    const bufferStartTime = Date.now();
    setLastBufferStart(bufferStartTime);
    
    // 设置定时器，在BUFFER_THRESHOLD时间后检查是否仍在缓冲
    bufferTimerRef.current = setTimeout(() => {
      if (lastBufferStart === bufferStartTime) {
        setIsBuffering(true);
        updateLog('[VideoPlayer] 显示加载指示器（缓冲时间超过阈值）');
      }
    }, BUFFER_THRESHOLD);
  };

  // 处理缓冲结束
  const handleBufferEnd = () => {
    // 清除之前的定时器
    if (bufferTimerRef.current) {
      clearTimeout(bufferTimerRef.current);
      bufferTimerRef.current = null;
    }
    
    // 重置缓冲开始时间并隐藏指示器
    setLastBufferStart(null);
    setIsBuffering(false);
  };

  // 组件卸载时清除定时器
  useEffect(() => {
    return () => {
      if (bufferTimerRef.current) {
        clearTimeout(bufferTimerRef.current);
      }
    };
  }, []);

  useEffect(() => { 
    if(!isVideoEnablePlay || !isVideoLoadMeta) {
      // 改变 .video-player-container 的背景色为白色
      document.querySelector('.video-player-container').style.backgroundColor = 'white';
    } else {
      // 改变 .video-player-container 的背景色为黑色
      document.querySelector('.video-player-container').style.backgroundColor = 'black';
    }
  }, [isVideoEnablePlay, isVideoLoadMeta]);

  // ====================
  // 渲染逻辑：使用 16:9 容器保持比例
  // ====================

  return (
    <div className="video-player-container">
      <ReactPlayer
        ref={videoRef}
        preload="auto"
        playsInline
        webkit-playsinline="true"
        x5-playsinline="true"
        x-webkit-airplay="allow"
        controls={false}
        muted={true}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          objectPosition: 'center 80%',
          opacity: isVideoEnablePlay ? 1 : 0.001,
          pointerEvents: isVideoEnablePlay ? 'auto' : 'none',
          visibility: isVideoEnablePlay ? 'visible' : 'hidden',
          zIndex: 1,
        }}
        onProgress={(e) => { 
          // checkVideoCacheEnabled();
        }}
        onWaiting={(e) => {
          updateLog('[VideoPlayer] Waiting for buffering...');
          handleBufferStart();
        }}
        onPlaying={(e) => {
          updateLog('[VideoPlayer] Playing...');
          handleBufferEnd();
        }}
        onCanPlay={(e) => { 
          updateLog('[VideoPlayer] Can play');
          handleBufferEnd();
        }}
        onSeeking={(e) => { 
          updateLog('[VideoPlayer] Seeking...');
          handleBufferStart();
          setIsSeeking(true);
        }}
        onSeeked={(e) => { 
          updateLog(`[VideoPlayer] Seeked, timeLength:${preSeekingTime.current ? preSeekingTime.current - new Date() : 0}`);
          setIsSeeking(false);
          handleBufferEnd();
        }}
        onError={(e) => {
          const time = new Date().toISOString();
          const error = e.target.error?.message || 'Unknown error';
          updateLog(`[VideoPlayer] 加载错误: ${error} - ${time}`);
          setIsVideoEnablePlay(false);
        }}
        onBuffer={(e) => {
          updateLog('[VideoPlayer] Buffering...');
        }}
        onBufferEnd={(e) => {
          updateLog('[VideoPlayer] Buffer ended');
        }}
      />

      {/* 新增：视频缓冲时的Loading指示器 */}
      {isVideoEnablePlay && isMediaReady && isBuffering && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(255, 255, 255, 0.3)',
              borderTop: '3px solid #fff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          ></div>
        </div>
      )}

      {/* 老师信息覆盖层 */}
      {(!isVideoEnablePlay || !isVideoLoadMeta) && (
        <div
          className="teacher-info"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            textAlign: 'center',
            padding: '20px',
            boxSizing: 'border-box',
          }}
        >
          {teacherInfo ? (
            <div> 
              <div className="teacher-bg">
                  <img className="teacher-blur-img"
                      src={teacherInfo.avatarUrl}
                      alt={teacherInfo.teacherName} />
              </div>
              <div className="teacher-content">
                  <div className="teacher-avatar">
                      <img src={teacherInfo.avatarUrl}
                          alt={teacherInfo.teacherName} />
                  </div>
                  <div className="teacher-name">老师：{teacherInfo.teacherName}</div>
              </div>
            </div>
          ) : (
            <p>暂无老师信息</p>
          )}
        </div>
      )}

      {/* 添加旋转动画的样式 */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default VideoPlayer;